import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import axios from '../api/axiosConfig';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import './Caja.css';

const Caja = () => {
  const { user } = useContext(AuthContext);
  const [balance, setBalance] = useState(null);
  const [movements, setMovements] = useState([]);
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [incomeForm, setIncomeForm] = useState({ description: '', amount: '', payment_source: '', created_at: new Date().toISOString().split('T')[0] });
  const [expenseForm, setExpenseForm] = useState({ description: '', amount: '', payment_source: '', created_at: new Date().toISOString().split('T')[0] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('');
  
  // Función para formatear el valor mientras se escribe (miles)
  const formatInputAmount = (value) => {
    if (value === undefined || value === null || value === '') return '';
    const numericValue = value.toString().replace(/\D/g, '');
    return new Intl.NumberFormat('es-CO').format(numericValue);
  };

  const handleAmountInputChange = (e, formType) => {
    const rawValue = e.target.value.replace(/\D/g, '');
    if (formType === 'income') {
      setIncomeForm({ ...incomeForm, amount: rawValue });
    } else {
      setExpenseForm({ ...expenseForm, amount: rawValue });
    }
  };

  // Estado para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Rango de fechas por defecto (mes actual)
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  // Obtener saldo actual
  const fetchBalance = useCallback(async () => {
    try {
      const response = await axios.get(`/api/cash/balance?startDate=${startDate}&endDate=${endDate}`);
      console.log('Balance recibido completo:', response.data);
      
      // La API puede devolver { balance: { balance: 100, ... } } o { balance: 100, ... }
      const data = response.data.balance !== undefined && typeof response.data.balance === 'object' 
        ? response.data.balance 
        : response.data;

      setBalance({
        balance: parseFloat(data.balance) || 0,
        balance_abonos: parseFloat(data.balance_abonos) || 0,
        total_ingresos: parseFloat(data.total_ingresos) || 0,
        total_egresos: parseFloat(data.total_egresos) || 0,
        period_ingresos: parseFloat(data.period_ingresos) || 0,
        period_egresos: parseFloat(data.period_egresos) || 0,
        period_abonos: parseFloat(data.period_abonos) || 0,
        period_egresos_cartera: parseFloat(data.period_egresos_cartera) || 0,
        ganancia_total: parseFloat(data.ganancia_total) || 0,
        cartera_pendiente: parseFloat(data.cartera_pendiente) || 0,
        cartera_pagada: parseFloat(data.cartera_pagada) || 0,
        cupl_count: parseInt(data.cupl_count) || 0,
        total_pendiente_global: parseFloat(data.total_pendiente_global) || 0,
        portfolio_by_month: data.portfolio_by_month || []
      });
    } catch (error) {
      console.error('Error al obtener saldo:', error);
      setError('Error al obtener saldo de caja');
    }
  }, [startDate, endDate]);

  // Obtener movimientos de caja
  const fetchMovements = useCallback(async () => {
    try {
      setLoading(true);
      let url = `/api/cash/movements?startDate=${startDate}&endDate=${endDate}`;
      if (filterType) {
        url += `&type=${filterType}`;
      }
      const response = await axios.get(url);
      setMovements(response.data);
      setError(null);
    } catch (error) {
      console.error('Error al obtener movimientos:', error);
      setError('Error al obtener movimientos de caja');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, filterType]);

  // Cargar datos al inicio
  useEffect(() => {
    fetchBalance();
    fetchMovements();
  }, [fetchBalance, fetchMovements]);

  // Manejar envío de ingreso
  const handleIncomeSubmit = async (e) => {
    e.preventDefault();

    if (!incomeForm.description.trim() || !incomeForm.amount || incomeForm.amount <= 0) {
      setError('Por favor complete todos los campos correctamente');
      return;
    }

    try {
      const payload = {
        ...incomeForm,
        amount: parseFloat(incomeForm.amount),
        type: 'ingreso'
      };
      await axios.post('/api/cash/movement', payload);

      // Limpiar formulario
      setIncomeForm({ description: '', amount: '', payment_source: '', created_at: new Date().toISOString().split('T')[0] });
      setShowIncomeModal(false);
      setError(null);

      // Recargar datos
      fetchBalance();
      fetchMovements();
    } catch (error) {
      console.error('Error al registrar ingreso:', error);
      setError('Error al registrar ingreso');
    }
  };

  // Registrar egreso manual
  const handleAddExpense = async (e) => {
    e.preventDefault();

    if (!expenseForm.description.trim() || !expenseForm.amount || expenseForm.amount <= 0) {
      setError('Por favor complete todos los campos correctamente');
      return;
    }

    try {
      const payload = {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount),
        type: 'egreso'
      };
      await axios.post('/api/cash/movement', payload);
      setShowExpenseModal(false);
      setExpenseForm({ description: '', amount: '', payment_source: '', created_at: new Date().toISOString().split('T')[0] });
      fetchBalance();
      fetchMovements();
    } catch (error) {
       console.error('Error al registrar egreso:', error);
       setError('Error al registrar egreso');
     }
   };

  const formatCurrency = (value) => {
    // Asegurar que el valor sea numérico
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '$ 0';

    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(numValue);
  };

  // Lógica de paginación para movimientos
  const filteredMovements = movements.filter(m => ['ingreso', 'egreso'].includes(m.type));
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredMovements.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  // Resetear página cuando cambien los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, startDate, endDate]);

  return (
    <div className="app-layout">
      <Sidebar userRole={user?.role} />

      <div className="main-content">
        <Navbar title="💰 Módulo de Caja" />

        <div className="page-content">
          {error && <div className="error-message">{error}</div>}

          {/* Filtros de Fecha */}
          <div className="date-filters" style={{ display: 'flex', gap: '20px', marginBottom: '25px', background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="filter-group">
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase' }}>Fecha Desde</label>
              <input 
                type="date" 
                value={startDate} 
                onChange={(e) => setStartDate(e.target.value)}
                style={{ padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', minWidth: '160px', fontSize: '14px' }}
              />
            </div>
            <div className="filter-group">
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase' }}>Fecha Hasta</label>
              <input 
                type="date" 
                value={endDate} 
                onChange={(e) => setEndDate(e.target.value)}
                style={{ padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', minWidth: '160px', fontSize: '14px' }}
              />
            </div>
            <div className="filter-info" style={{ marginLeft: 'auto', textAlign: 'right', background: '#f8fafc', padding: '10px 15px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
              <span style={{ color: '#64748b', fontSize: '12px', display: 'block' }}>Resumen del periodo:</span>
              <strong style={{ color: '#1e293b' }}>
                {new Date(startDate).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' })} - {new Date(endDate).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })}
              </strong>
            </div>
          </div>

          {/* Resumen de saldo GLOBAL */}
          {balance && (
            <>
              <h2 style={{ fontSize: '16px', color: '#64748b', marginBottom: '15px', textTransform: 'uppercase', letterSpacing: '1px' }}>📊 Totales Globales (Acumulado)</h2>
              <div className="balance-summary" style={{ marginBottom: '30px' }}>
                <div className="balance-card total">
                  <h3>Saldo Total en Caja</h3>
                  <p className="balance-amount">{formatCurrency(balance.balance)}</p>
                </div>
                <div className="balance-card income">
                  <h3>Total Ingresos Global</h3>
                  <p className="balance-amount">{formatCurrency(balance.total_ingresos)}</p>
                </div>
                <div className="balance-card expense">
                  <h3>Total Egresos Global</h3>
                  <p className="balance-amount">{formatCurrency(balance.total_egresos)}</p>
                </div>
              </div>

              {/* Resumen de Deuda por Mes */}
              <div style={{ background: '#fff1f2', padding: '20px', borderRadius: '12px', marginBottom: '40px', border: '1px solid #fecaca' }}>
                <h3 style={{ margin: '0 0 15px 0', color: '#b91c1c', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  🚩 Detalle de Deuda por Mes (Cartera Pendiente)
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
                  {balance.portfolio_by_month?.map((item, idx) => (
                    <div key={idx} style={{ background: 'white', padding: '12px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', borderLeft: item.pendiente > 0 ? '4px solid #ef4444' : '4px solid #10b981' }}>
                      <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase' }}>{item.month_name}</span>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '5px' }}>
                        <strong style={{ fontSize: '18px', color: item.pendiente > 0 ? '#b91c1c' : '#10b981' }}>{formatCurrency(item.pendiente)}</strong>
                        {item.pendiente === 0 && <span style={{ fontSize: '12px', color: '#10b981' }}>✅ Al día</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <h2 style={{ fontSize: '16px', color: '#64748b', marginBottom: '15px', textTransform: 'uppercase', letterSpacing: '1px' }}>📅 Resumen del Periodo Seleccionado</h2>
              <div className="balance-summary">
                <div className="balance-card portfolio-pending" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                  <h3>CUPLs Registrados</h3>
                  <p className="balance-amount" style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                    {balance.cupl_count} <span style={{ fontSize: '14px', opacity: 0.8 }}>unidades</span>
                  </p>
                </div>
                <div className="balance-card portfolio-pending">
                  <h3>Cartera Pendiente (Periodo)</h3>
                  <p className="balance-amount">{formatCurrency(balance.cartera_pendiente)}</p>
                </div>
                <div className="balance-card portfolio">
                  <h3>Cartera Cobrada (Periodo)</h3>
                  <p className="balance-amount">{formatCurrency(balance.cartera_pagada)}</p>
                </div>
              </div>
            </>
          )}

          <div className="caja-actions" style={{ display: 'flex', gap: '15px', marginBottom: '25px' }}>
            <button
              className="btn-primary"
              onClick={() => setShowIncomeModal(true)}
            >
              + Registrar Ingreso
            </button>
            <button
              className="btn-danger"
              onClick={() => setShowExpenseModal(true)}
              style={{ background: '#ef4444', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
            >
              - Registrar Egreso Principal
            </button>
          </div>

          {/* Modal de ingreso */}
          {showIncomeModal && (
            <div className="modal-overlay" onClick={() => setShowIncomeModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h2>Registrar Ingreso Manual</h2>
                <form onSubmit={handleIncomeSubmit}>
                  <div className="form-group">
                    <label>Fecha del Ingreso *</label>
                    <input
                      type="date"
                      value={incomeForm.created_at}
                      onChange={(e) => setIncomeForm({ ...incomeForm, created_at: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Descripción *</label>
                    <input
                      type="text"
                      placeholder="Ej: Pago por servicios"
                      value={incomeForm.description}
                      onChange={(e) =>
                        setIncomeForm({ ...incomeForm, description: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Cantidad *</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontWeight: 'bold' }}>$</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="0"
                        style={{ paddingLeft: '25px' }}
                        value={formatInputAmount(incomeForm.amount)}
                        onChange={(e) => handleAmountInputChange(e, 'income')}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>¿Dónde se pagó? *</label>
                    <select
                      value={incomeForm.payment_source}
                      onChange={(e) =>
                        setIncomeForm({ ...incomeForm, payment_source: e.target.value })
                      }
                      required
                      className="form-control"
                    >
                      <option value="">Seleccione una opción</option>
                      <option value="movi -- brayan rios">movi -- brayan rios</option>
                      <option value="movi -- isabela">movi -- isabela</option>
                      <option value="movi -- breyner">movi -- breyner</option>
                      <option value="Movilred -- zayda">Movilred -- zayda</option>
                      <option value="Movilservicios">Movilservicios</option>
                    </select>
                  </div>
                  <div className="modal-buttons">
                    <button type="submit" className="btn btn-primary">
                      Guardar Ingreso
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowIncomeModal(false)}
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Modal de egreso */}
          {showExpenseModal && (
            <div className="modal-overlay" onClick={() => setShowExpenseModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h2 style={{ color: '#ef4444' }}>Registrar Egreso Manual</h2>
                <form onSubmit={handleAddExpense}>
                  <div className="form-group">
                    <label>Fecha del Egreso *</label>
                    <input
                      type="date"
                      value={expenseForm.created_at}
                      onChange={(e) => setExpenseForm({ ...expenseForm, created_at: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Descripción *</label>
                    <input
                      type="text"
                      placeholder="Ej: Pago de servicios, papelería, etc."
                      value={expenseForm.description}
                      onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Cantidad *</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontWeight: 'bold' }}>$</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="0"
                        style={{ paddingLeft: '25px' }}
                        value={formatInputAmount(expenseForm.amount)}
                        onChange={(e) => handleAmountInputChange(e, 'expense')}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Medio de Pago (Opcional)</label>
                    <input
                      type="text"
                      placeholder="Ej: Efectivo, Nequi"
                      value={expenseForm.payment_source}
                      onChange={(e) => setExpenseForm({ ...expenseForm, payment_source: e.target.value })}
                    />
                  </div>
                  <div className="modal-buttons">
                    <button type="submit" className="btn btn-danger" style={{ background: '#ef4444' }}>
                      Guardar Egreso
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowExpenseModal(false)}
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            </div>
           )}

          {/* Filtro de movimientos */}
          <div className="caja-filters">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="filter-select"
            >
              <option value="">Todos los movimientos</option>
              <option value="ingreso">Solo Ingresos</option>
              <option value="egreso">Solo Egresos</option>
            </select>
          </div>

          {/* Tabla de movimientos */}
          <div className="movements-table-container">
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              📋 Movimientos Generales de Caja
            </h2>
            {loading ? (
              <p>Cargando movimientos...</p>
            ) : movements.filter(m => ['ingreso', 'egreso'].includes(m.type)).length === 0 ? (
              <p style={{ color: '#64748b', fontStyle: 'italic' }}>No hay ingresos o egresos generales en este periodo.</p>
            ) : (
              <table className="movements-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Descripción</th>
                    <th>Origen/Medio</th>
                    <th>Cantidad</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((movement) => (
                    <tr key={movement.id} className={movement.type === 'ingreso' ? 'income-row' : 'expense-row'}>
                      <td data-label="Fecha">{new Date(movement.created_at).toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' })}</td>
                      <td data-label="Descripción">{movement.description}</td>
                      <td data-label="Origen/Medio">{movement.payment_source || '-'}</td>
                      <td data-label="Cantidad" className={movement.type === 'ingreso' ? 'amount-income' : 'amount-expense'}>
                        {movement.type === 'ingreso' ? '+' : '-'} {formatCurrency(movement.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            
            <Pagination 
              currentPage={currentPage}
              totalItems={filteredMovements.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Caja;
